/**
 * Раздел «Экономика» страницы «Аналитика» (redesign по макету): заголовок
 * секции с линией и иконкой + ряд стат-карточек + карточка
 * «Экономическое развитие ТОП-4» (строки с барами, ИФО и объёмом) +
 * подраздел «Развитие МСБ» (pie «зарегистрированные субъекты по видам
 * деятельности», финансово-кредитная поддержка).
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsEconomy.json; остальные КАТО (по выбранному
 * в сайдбаре) — один запрос /api/public/analytics/economy (DP-127,
 * US-04.3):
 *  - стат-карточки — plashka (ЗП, ИФО+объём инвестиций/промышленности,
 *    исполнение бюджета, налоговые поступления факт);
 *  - «ТОП-4» — economic_development (СЭР): топ-4 отрасли по объёму;
 *  - «Освоение бюджета» — budget_execution (итого / расходы районов /
 *    расходы МСУ; «факт» = план × % освоения);
 *  - «Доходы и налоговые поступления» — первая строка income_tax
 *    (налоговые / неналоговые / продажа осн. капитала, факт/план за
 *    период; исполнение = факт / план); порядок строк фиксированный —
 *    налоговые → неналоговые → продажа основного капитала;
 *  - «Развитие МСБ» — msb: pie «Зарегистрированные субъекты МСБ по видам
 *    деятельности» (доли — activities; registered_count — плашкой над
 *    разбивкой, действующие — подписью в шапке), финансово-кредитная
 *    поддержка (financial: вид, кол-во проектов, сумма).
 */
import type { LucideIcon } from 'lucide-react';
import { Cog, Coins, Factory, Wheat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import { cn } from '../../../lib/cn';

import { LegalEntityTaxes } from './LegalEntityTaxes';
import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import analyticsEconomy from '../../../hardData/analyticsEconomy.json';
import { getAnalyticsEconomy } from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type { EconomyAnalyticsResponse } from '../../../types/analytics';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

const fmtTrend = (v: number): string => `${v > 0 ? '+' : ''}${v}`;

const TOP4_ICONS: Record<string, LucideIcon> = {
  investments: Coins,
  industry: Factory,
  manufacturing: Cog,
  agriculture: Wheat,
};

/** subtype записей СЭР → ключ подписи digitalPassport.serTable.rows.*. */
const SER_SUBTYPE_KEYS: Record<string, string> = {
  INVESTMENT: 'investments',
  INDUSTRIAL_PRODUCTION: 'industry',
  MINING_INDUSTRY: 'mining',
  MANUFACTURING_INDUSTRY: 'manufacturing',
  CONSTRUCTION: 'construction',
  AGRICULTURE: 'agriculture',
  TRANSPORT_WAREHOUSING: 'transport',
  HOUSE_COMMISSIONING: 'houseCommissioning',
};

interface Top4RowVM {
  key: string;
  ifo: string;
  amount: string;
  barPercent: number;
}

interface EconCardVM {
  key: string;
  value: string;
  unit: string | null;
  trend: { text: string } | null;
  footnotes: Array<{ text: string; bold?: boolean }>;
}

/** Палитра долей pie «виды деятельности МСБ» — как соседние pie-чарты. */
const MSB_COLORS = [
  '#4A63E7',
  '#2C9C42',
  '#D89B1F',
  '#8FA3F2',
  '#A620F0',
  '#E87BA4',
  '#5FC2C9',
  '#F2884B',
];

/** Доля pie «Зарегистрированные субъекты МСБ по видам деятельности». */
interface MsbSegmentVM {
  key: string;
  name: string;
  count: number;
  color: string;
}

/**
 * Порядок строк «Доходов и налоговых поступлений»: налоговые →
 * неналоговые → поступления от продажи основного капитала (остальные
 * категории — в конце в порядке ответа). Названия приходят готовыми
 * строками из analytics-БД, поэтому сопоставляем по ключевым словам.
 */
const incomeRowOrder = (name: string): number => {
  const n = name.toLowerCase();
  if (n.includes('неналог') || n.includes('салықтық емес')) return 1;
  if (n.includes('налог') || n.includes('салық')) return 0;
  if (n.includes('продаж') || n.includes('сату')) return 2;
  return 3;
};

interface BudgetRowVM {
  key: string;
  name: string;
  execution: string;
  /** Годовой план (только у строк из analytics-БД, DP-134). */
  planYearly: string | null;
  plan: string;
  fact: string;
  highlight: boolean;
}

const pct = (v: number | null): string => (v !== null ? `${fmt1(v)}%` : '—');

interface EconomySectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты — «ТОП-4»,
   * «Освоение бюджета», «Доходы и налоговые поступления» (FR-2.2). */
  summary?: boolean;
}

export const EconomySection = ({
  summary = false,
}: EconomySectionProps = {}) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.resolvedLanguage === 'kz';
  const {
    cards,
    top4,
    budget,
    msb,
    legalTaxes: mockLegalTaxes,
  } = analyticsEconomy;

  const { data, loading, isHardKato } =
    useAnalyticsSection<EconomyAnalyticsResponse>(
      'economy',
      getAnalyticsEconomy,
    );

  // --- Вью-модели: мок для демо-КАТО, /analytics/economy для остальных ---

  const plashka = data?.plashka;

  // Карточка «ИФО + объём» (инвестиции / промышленность): значение — ИФО,
  // сноска — объём в млн тг.
  const ifoCard = (
    key: string,
    ifo: number | null,
    val: number | null,
  ): EconCardVM => ({
    key,
    value: ifo !== null ? `${fmt1(ifo)} %` : '—',
    unit: t('digitalPassport.serTable.columnIfo'),
    trend: null,
    footnotes:
      val !== null
        ? [{ text: `${fmt1(val)} ${t('units.mlnTenge')}`, bold: true }]
        : [],
  });

  const cardVMs: EconCardVM[] = isHardKato
    ? cards.map((card) => ({
        key: card.key,
        value: card.value,
        unit: card.unit ? (isKz ? card.unit.kz : card.unit.ru) : null,
        trend: card.trend != null ? { text: fmtTrend(card.trend) } : null,
        footnotes: card.footnotes.map((note) => ({
          text: isKz ? note.kz : note.ru,
          bold: note.bold,
        })),
      }))
    : [
        {
          key: 'avgSalary',
          value: plashka?.avg_salary != null ? fmt(plashka.avg_salary) : '—',
          unit: t('units.tenge'),
          trend: null,
          footnotes: [],
        },
        ifoCard(
          'investments',
          plashka?.investment_ifo ?? null,
          plashka?.investment_val ?? null,
        ),
        {
          key: 'budgetExecution',
          value: pct(plashka?.budget_execution_pct ?? null),
          unit: null,
          trend: null,
          footnotes: [],
        },
        {
          key: 'taxRevenue',
          value:
            plashka?.tax_revenues_fact != null
              ? fmt1(plashka.tax_revenues_fact)
              : '—',
          unit: plashka?.tax_revenues_fact != null ? t('units.mlnTenge') : null,
          trend: null,
          footnotes: [],
        },
        ifoCard(
          'industrialProduction',
          plashka?.industry_ifo ?? null,
          plashka?.industry_val ?? null,
        ),
      ];

  const amt = (v: number | null): string => (v !== null ? fmt(v) : '—');
  // Исполнение = факт / план за период (нет пары → «—»).
  const execPct = (fact: number | null, plan: number | null): string =>
    fact !== null && plan !== null && plan !== 0
      ? pct((fact / plan) * 100)
      : '—';

  // «Доходы и налоговые поступления»: демо-КАТО — мок; остальные —
  // budget_income из analytics-БД (DP-134: налоговые / неналоговые /
  // продажа основного капитала), fallback — legacy income_tax.
  const revenueRows: BudgetRowVM[] = isHardKato
    ? budget.revenue.rows.map((row) => ({
        key: row.key,
        name: isKz ? row.name.kz : row.name.ru,
        execution: row.execution,
        planYearly: null,
        plan: row.plan,
        fact: row.fact,
        highlight: row.highlight,
      }))
    : (() => {
        // Порядок категорий из БД не гарантирован — выстраиваем в
        // привычный: налоговые → неналоговые → продажа осн. капитала.
        const income = [...(data?.budget_income ?? [])].sort(
          (a, b) => incomeRowOrder(a.ktk_name) - incomeRowOrder(b.ktk_name),
        );
        if (income.length > 0) {
          return income.map((row, index) => ({
            key: `income-${index}`,
            // Названия категорий приходят готовыми строками из analytics-БД.
            name: row.ktk_name,
            execution: pct(row.execution_pct),
            planYearly: amt(row.plan_yearly),
            plan: amt(row.plan_period),
            fact: amt(row.fact_period),
            highlight: false,
          }));
        }
        const it = data?.income_tax[0];
        if (!it) return [];
        const defs: Array<[string, number | null, number | null]> = [
          ['tax', it.tax_revenues_fact, it.tax_revenues_plan],
          ['nonTax', it.non_tax_revenues_fact, it.non_tax_revenues_plan],
          ['assetSales', it.fixed_assets_sale_fact, it.fixed_assets_sale_plan],
        ];
        return defs.flatMap(([key, fact, plan]) => {
          if (fact === null && plan === null) return [];
          return [
            {
              key,
              name: t(`analytics.economy.budget.rows.${key}`),
              execution: execPct(fact, plan),
              planYearly: null,
              plan: amt(plan),
              fact: amt(fact),
              highlight: false,
            },
          ];
        });
      })();

  // «Освоение бюджета»: демо-КАТО — мок; остальные — budget_expenditure
  // из analytics-БД (DP-134: Итого / Район / МСУ, «Итого» первой строкой
  // считает бэк — выделяем), fallback — legacy budget_execution.
  const executionRows: BudgetRowVM[] = isHardKato
    ? budget.execution.rows.map((row) => ({
        key: row.key,
        name: isKz ? row.name.kz : row.name.ru,
        execution: row.execution,
        planYearly: null,
        plan: row.plan,
        fact: row.fact,
        highlight: row.highlight,
      }))
    : (() => {
        const expenditure = data?.budget_expenditure ?? [];
        if (expenditure.length > 0) {
          return expenditure.map((row, index) => ({
            key: `expenditure-${index}`,
            name: row.name,
            execution: pct(row.execution_pct),
            planYearly: amt(row.plan_yearly),
            plan: amt(row.plan_period),
            fact: amt(row.fact_period),
            highlight: index === 0,
          }));
        }
        const be = data?.budget_execution;
        if (!be) return [];
        const defs: Array<[string, number | null, number | null, boolean]> = [
          ['total', be.total_cur_year_budget, be.total_budget_processing, true],
          [
            'districts',
            be.cur_year_dev_budget,
            be.dev_budget_processing,
            false,
          ],
          [
            'msu',
            be.cur_year_active_budget,
            be.active_budget_processing,
            false,
          ],
        ];
        return defs.flatMap(([key, plan, processing, highlight]) => {
          if (plan === null && processing === null) return [];
          const fact =
            plan !== null && processing !== null
              ? (plan * processing) / 100
              : null;
          return [
            {
              key,
              name: t(`analytics.economy.budget.rows.${key}`),
              execution: pct(processing),
              planYearly: null,
              plan: plan !== null ? fmt1(plan) : '—',
              fact: fact !== null ? fmt1(fact) : '—',
              highlight,
            },
          ];
        });
      })();


  // «ТОП-4»: economic_development (СЭР) — топ-4 отрасли по объёму
  // текущего года; бары — доля от максимального объёма.
  const top4Rows: Top4RowVM[] = isHardKato
    ? top4.rows
    : (() => {
        const entries = (data?.economic_development ?? [])
          .flatMap((stat) => {
            const key = SER_SUBTYPE_KEYS[stat.subtype];
            if (
              key === undefined ||
              (stat.ifo === null && stat.current_year_val === null)
            ) {
              return [];
            }
            return [{ key, ifo: stat.ifo, amount: stat.current_year_val }];
          })
          .sort((a, b) => (b.amount ?? -Infinity) - (a.amount ?? -Infinity))
          .slice(0, 4);
        const maxAmount = Math.max(
          1,
          ...entries.map((e) => Math.abs(e.amount ?? 0)),
        );
        return entries.map((e) => ({
          key: e.key,
          ifo: pct(e.ifo),
          amount: e.amount !== null ? fmt1(e.amount) : '—',
          barPercent:
            e.amount !== null
              ? Math.max(2, (Math.abs(e.amount) / maxAmount) * 100)
              : 0,
        }));
      })();

  // --- «Развитие МСБ»: мок для демо-КАТО, msb ответа для остальных ---

  // Доли pie — виды деятельности (activities ответа; подписи приходят
  // готовыми строками из БД), по убыванию количества. Демо-КАТО — те же
  // виды из мока.
  const msbSegments: MsbSegmentVM[] = (
    isHardKato
      ? msb.directions.rows.map((row) => ({
          key: row.key,
          name: isKz ? row.name.kz : row.name.ru,
          count: row.count,
        }))
      : (data?.msb?.activities ?? []).flatMap((row) =>
          row.count === null
            ? []
            : [{ key: row.activity, name: row.activity, count: row.count }],
        )
  )
    .sort((a, b) => b.count - a.count)
    .map((row, index) => ({
      ...row,
      color: MSB_COLORS[index % MSB_COLORS.length],
    }));

  // Нулевые виды остаются в легенде, но не рисуются долями.
  const msbPieSegments = msbSegments.filter((segment) => segment.count > 0);

  // Центр pie — общее количество зарегистрированных субъектов МСБ
  // (subjects.registered_count; нет значения — сумма долей).
  const msbSegmentsSum = msbSegments.reduce((acc, row) => acc + row.count, 0);
  const msbRegistered = isHardKato
    ? msb.subjects.registeredCount
    : (data?.msb?.subjects?.registered_count ?? msbSegmentsSum);
  // Действующие субъекты — подписью в шапке карточки (отдельного виджета
  // под них больше нет).
  const msbActive = isHardKato
    ? msb.subjects.activeCount
    : (data?.msb?.subjects?.active_count ?? null);

  const msbChartConfig = Object.fromEntries(
    msbSegments.map((segment) => [
      segment.key,
      { label: segment.name, color: segment.color },
    ]),
  ) satisfies ChartConfig;

  // Финансово-кредитная поддержка — таблица «категория / кол-во проектов /
  // сумма» + итоговая строка (оба режима). Демо-КАТО — фиксированные
  // категории из i18n; остальные — financial ответа (вид — готовая строка
  // из БД); нет значения → «—».
  const msbSupport: {
    rows: Array<{ key: string; name: string; projects: string; sum: string }>;
    total: { projects: string; sum: string } | null;
  } = (() => {
    const cell = (v: number | null): string => (v !== null ? fmt(v) : '—');
    if (isHardKato) {
      return {
        rows: msb.support.rows.map((row) => ({
          key: row.key,
          name: t(`analytics.economy.msb.support.rows.${row.key}`),
          projects: cell(row.projects),
          sum: cell(row.sum),
        })),
        total: {
          projects: cell(msb.support.totalProjects),
          sum: cell(msb.support.totalSum),
        },
      };
    }
    const financial = data?.msb?.financial ?? [];
    if (financial.length === 0) return { rows: [], total: null };
    const totalProjects = financial.some((row) => row.projects !== null)
      ? financial.reduce((acc, row) => acc + (row.projects ?? 0), 0)
      : null;
    const totalSum = financial.some((row) => row.amount !== null)
      ? financial.reduce((acc, row) => acc + (row.amount ?? 0), 0)
      : null;
    return {
      rows: financial.map((row) => ({
        key: row.kind,
        name: row.kind,
        projects: cell(row.projects),
        sum: cell(row.amount),
      })),
      total: { projects: cell(totalProjects), sum: cell(totalSum) },
    };
  })();

  return (
    <section className="space-y-4" data-testid="analytics-economy">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.economy.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Coins
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>
          {!summary && (
            <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-5">
              {cardVMs.map((card) => (
                <StatCard
                  key={card.key}
                  testId={`analytics-card-${card.key}`}
                  title={t(`analytics.economy.cards.${card.key}`)}
                  value={card.value}
                  unit={card.unit}
                  trend={card.trend}
                  footnotes={card.footnotes}
                />
              ))}
            </div>
          )}

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>{t('analytics.economy.top4.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {top4Rows.length === 0 ? (
                <p
                  data-testid="block-placeholder"
                  className="text-sm lowercase text-text-muted"
                >
                  {t('common.noData')}
                </p>
              ) : (
                <div data-testid="analytics-economy-top4">
                  <div className="flex items-center gap-3 pb-2 text-sm text-text-faint">
                    <span className="min-w-0 flex-1" />
                    <span className="w-16 shrink-0 text-right">
                      {t('digitalPassport.serTable.columnIfo')}
                    </span>
                    <span className="w-20 shrink-0 text-right">
                      {t('analytics.economy.top4.columnAmount')}
                    </span>
                  </div>

                  <ul>
                    {top4Rows.map((row) => {
                      const Icon = TOP4_ICONS[row.key] ?? Coins;
                      return (
                        <li
                          key={row.key}
                          data-testid={`analytics-top4-row-${row.key}`}
                          className="flex items-center gap-3 border-t border-surface-border py-3.5 text-sm"
                        >
                          <span className="flex min-w-0 flex-1 items-center gap-3 sm:flex-none sm:basis-1/3">
                            <Icon
                              strokeWidth={1.7}
                              className="size-6 shrink-0 text-brand-500"
                            />
                            <span className="truncate font-semibold">
                              {t(`digitalPassport.serTable.rows.${row.key}`)}
                            </span>
                          </span>
                          <span className="hidden h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted sm:block">
                            <span
                              className="block h-full rounded-full bg-chart-blue"
                              style={{ width: `${row.barPercent}%` }}
                              data-testid={`analytics-top4-row-${row.key}-bar`}
                            />
                          </span>
                          <span className="w-16 shrink-0 text-right font-bold tabular-nums text-chart-indigo">
                            {row.ifo}
                          </span>
                          <span className="w-20 shrink-0 text-right font-extrabold tabular-nums">
                            {row.amount}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-3.5 xl:grid-cols-2">
            {(['execution', 'revenue'] as const).map((tableKey) => {
              const table = budget[tableKey];
              const rows: BudgetRowVM[] =
                tableKey === 'revenue' ? revenueRows : executionRows;
              // Годовой план есть только у строк из analytics-БД (DP-134).
              const hasPlanYearly = rows.some((row) => row.planYearly !== null);
              return (
                <Card key={tableKey} className="shadow-card">
                  <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
                    <CardTitle>
                      {t(`analytics.economy.budget.${tableKey}Title`)}
                    </CardTitle>
                    <span className="shrink-0 text-sm text-text-faint">
                      {isHardKato
                        ? isKz
                          ? table.period.kz
                          : table.period.ru
                        : t('units.mlnTenge')}
                    </span>
                  </CardHeader>
                  <CardContent>
                    {rows.length === 0 ? (
                      <p
                        data-testid="block-placeholder"
                        className="text-sm lowercase text-text-muted"
                      >
                        {t('common.noData')}
                      </p>
                    ) : (
                      <div data-testid={`analytics-budget-${tableKey}`}>
                        <div className="flex items-center gap-3 px-3 pb-2 text-sm leading-tight text-text-faint">
                          <span className="min-w-0 flex-1" />
                          <span className="w-24 shrink-0 text-right">
                            {t('analytics.economy.budget.columnExecution')}
                          </span>
                          {hasPlanYearly && (
                            <span className="w-20 shrink-0 text-right">
                              {t('analytics.economy.budget.columnPlanYearly')}
                            </span>
                          )}
                          <span className="w-20 shrink-0 text-right">
                            {t('analytics.economy.budget.columnPlan')}
                          </span>
                          <span className="w-20 shrink-0 text-right">
                            {t('analytics.economy.budget.columnFact')}
                          </span>
                        </div>
                        <ul>
                          {rows.map((row) => (
                            <li
                              key={row.key}
                              data-testid={`analytics-budget-${tableKey}-${row.key}`}
                              className={cn(
                                'flex items-center gap-3 px-3 py-4 text-sm',
                                row.highlight
                                  ? 'rounded-lg bg-surface-muted/60'
                                  : 'border-b border-surface-border last:border-b-0 last:pb-0',
                              )}
                            >
                              <span
                                className={cn(
                                  'min-w-0 flex-1 leading-snug',
                                  row.highlight
                                    ? 'font-extrabold'
                                    : 'font-semibold',
                                )}
                              >
                                {row.name}
                              </span>
                              <span className="w-24 shrink-0 text-right font-bold tabular-nums text-chart-indigo">
                                {row.execution}
                              </span>
                              {hasPlanYearly && (
                                <span className="w-20 shrink-0 text-right font-bold tabular-nums">
                                  {row.planYearly ?? '—'}
                                </span>
                              )}
                              <span className="w-20 shrink-0 text-right font-extrabold tabular-nums">
                                {row.plan}
                              </span>
                              <span className="w-20 shrink-0 text-right font-bold tabular-nums text-brand-600">
                                {row.fact}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {!summary && (
            <>
              {/* «Налоговые поступления юридических лиц» (FR-3a) — после бюджетных
          виджетов, перед «Развитием МСБ». */}
              {/* Демо-КАТО — мок формы 2-47; остальные — ресты
                  /analytics/legal-entity-taxes* (виджет грузит их сам). */}
              <LegalEntityTaxes demo={isHardKato ? mockLegalTaxes : null} />

              <div className="flex items-center gap-4 pt-2">
                <h3 className="shrink-0 text-lg font-extrabold">
                  {t('analytics.economy.msb.title')}
                </h3>
                <div
                  className="h-px flex-1 bg-surface-border"
                  aria-hidden="true"
                />
              </div>

              {/* Субъекты МСБ и их виды деятельности — один виджет: pie с долями
          видов деятельности (количество — в легенде и в тултипе по
          наведению); общее число зарегистрированных субъектов — в центре
          pie и однострочной плашкой над разбивкой; единицы колонок
          («ед.» / «%») — над столбцами легенды (замечания ревью). */}
              <Card className="shadow-card">
                <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
                  <CardTitle>
                    {t('analytics.economy.msb.subjects.title')}
                  </CardTitle>
                  {msbActive !== null && (
                    <span className="shrink-0 text-sm text-text-faint">
                      {t('analytics.economy.msb.subjects.activeLabel')}:{' '}
                      <span
                        className="font-bold text-text"
                        data-testid="analytics-msb-active"
                      >
                        {fmt(msbActive)}
                      </span>
                    </span>
                  )}
                </CardHeader>
                <CardContent>
                  {msbSegments.length === 0 ? (
                    <p
                      data-testid="block-placeholder"
                      className="text-sm lowercase text-text-muted"
                    >
                      {t('common.noData')}
                    </p>
                  ) : (
                    <div
                      className="flex flex-wrap items-center justify-center gap-6"
                      data-testid="analytics-msb-subjects"
                    >
                      <div className="relative w-full max-w-[220px] shrink-0">
                        <ChartContainer
                          config={msbChartConfig}
                          className="aspect-square"
                        >
                          <PieChart>
                            <ChartTooltip
                              cursor={false}
                              content={<ChartTooltipContent hideLabel />}
                            />
                            <Pie
                              data={msbPieSegments.map((segment) => ({
                                key: segment.key,
                                value: segment.count,
                              }))}
                              dataKey="value"
                              nameKey="key"
                              innerRadius="58%"
                              outerRadius="98%"
                              strokeWidth={2}
                            >
                              {msbPieSegments.map((segment) => (
                                <Cell key={segment.key} fill={segment.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ChartContainer>
                        {/* Общее число зарегистрированных — в центре pie
                    (замечание ревью). */}
                        <div
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                          aria-hidden="true"
                        >
                          <span className="text-2xl font-extrabold tabular-nums">
                            {fmt(msbRegistered)}
                          </span>
                        </div>
                      </div>

                      <div className="flex min-w-56 flex-1 flex-col gap-4">
                        {/* Общее число зарегистрированных субъектов — однострочной
                    плашкой над разбивкой (замечание ревью). */}
                        <div
                          className="flex items-baseline gap-2 rounded-lg bg-brand-50 px-4 py-2.5 text-sm"
                          data-testid="analytics-msb-registered"
                        >
                          <span className="min-w-0 leading-snug text-text-muted">
                            {t(
                              'analytics.economy.msb.subjects.registeredLabel',
                            )}
                          </span>
                        </div>

                        <div className="min-w-0">
                          {/* Единицы измерения — над столбцами легенды. */}
                          <div className="flex items-baseline gap-2 pb-1.5 text-xs text-text-faint">
                            <span className="min-w-0 flex-1" />
                            <span className="w-16 shrink-0 text-right">
                              {t('units.pieces')}
                            </span>
                            <span className="w-12 shrink-0 text-right">%</span>
                          </div>
                          <ul
                            className="flex flex-col gap-2"
                            data-testid="analytics-msb-directions"
                          >
                            {msbSegments.map((segment) => (
                              <li
                                key={segment.key}
                                data-testid={`analytics-msb-direction-${segment.key}`}
                                className="flex items-baseline gap-2 text-sm"
                              >
                                <span
                                  className="size-2.5 shrink-0 self-center rounded-sm"
                                  style={{ backgroundColor: segment.color }}
                                  aria-hidden="true"
                                />
                                <span className="min-w-0 leading-snug text-text-muted">
                                  {segment.name}
                                </span>
                                <span className="flex-1 border-b border-dotted border-surface-border" />
                                <span className="w-16 shrink-0 whitespace-nowrap text-right font-extrabold tabular-nums">
                                  {fmt(segment.count)}
                                </span>
                                <span className="w-12 shrink-0 text-right text-xs tabular-nums text-text-faint">
                                  {msbSegmentsSum > 0
                                    ? `${fmt1((segment.count / msbSegmentsSum) * 100)}%`
                                    : '—'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle>
                    {t('analytics.economy.msb.support.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {msbSupport.rows.length === 0 ? (
                    <p
                      data-testid="block-placeholder"
                      className="text-sm lowercase text-text-muted"
                    >
                      {t('common.noData')}
                    </p>
                  ) : (
                    <div data-testid="analytics-msb-support">
                      <div className="flex items-center gap-3 px-3 pb-2 text-sm text-text-faint">
                        <span className="min-w-0 flex-1">
                          {t('analytics.economy.msb.support.columnCategory')}
                        </span>
                        <span className="w-28 shrink-0 text-right">
                          {t('analytics.economy.msb.support.columnProjects')}
                        </span>
                        <span className="w-32 shrink-0 text-right">
                          {t('analytics.economy.msb.support.columnSum')}
                        </span>
                      </div>
                      <ul>
                        {msbSupport.rows.map((row) => (
                          <li
                            key={row.key}
                            data-testid={`analytics-msb-support-${row.key}`}
                            className="flex items-center gap-3 border-b border-surface-border px-3 py-4 text-sm last:border-b-0"
                          >
                            <span className="min-w-0 flex-1 font-medium leading-snug">
                              {row.name}
                            </span>
                            <span className="w-28 shrink-0 text-right font-extrabold tabular-nums">
                              {row.projects}
                            </span>
                            <span className="w-32 shrink-0 text-right font-extrabold tabular-nums">
                              {row.sum}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {msbSupport.total && (
                        <div
                          className="mt-1 flex items-center gap-3 rounded-lg bg-surface-muted/60 p-3 text-sm font-extrabold"
                          data-testid="analytics-msb-support-total"
                        >
                          <span className="min-w-0 flex-1 uppercase">
                            {t('analytics.economy.msb.support.rows.total')}
                          </span>
                          <span className="w-28 shrink-0 text-right tabular-nums">
                            {msbSupport.total.projects}
                          </span>
                          <span className="w-32 shrink-0 text-right tabular-nums">
                            {msbSupport.total.sum}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </section>
  );
};
